/** @jsx jsx */
import { jsx } from "@emotion/react";
import { useRef } from "react";
import {
  FacebookShareButton,
  TwitterShareButton,
  TumblrShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  WhatsappShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  TumblrIcon,
  LinkedinIcon,
  TelegramIcon,
  WhatsappIcon,
  EmailIcon,
} from "react-share";
import { CopyToClipboard } from "react-copy-to-clipboard";
import siteConfig from "../../../data/siteConfig";
import LinkIcon from "../../../static/svg/link.svg";
import PropsShare from "../../types/PropsShare";

const Share = (props: PropsShare) => {
  const { data } = props;
  const { title, slug, color } = data;

  const url = siteConfig.url + slug;
  const popup = useRef<HTMLDivElement>(null);

  // !important is needed here because of the react-share module
  const buttonCss = {
    borderColor: "white !important",
    borderStyle: "solid !important",
    borderWidth: "0.1em !important",
    borderRadius: "50% !important",
    lineHeight: "0 !important",
    cursor: "initial !important",
    width: "3.5rem",
    height: "3.5rem",
    margin: "0.5rem",
    backgroundColor: `${color} !important`,
    fill: "white",
    transitionDuration: "0.3s",
    transitionProperty: "background-color fill",
    "&:hover, &:focus, &:active": {
      backgroundColor: "white !important",
      fill: color,
    },
  };

  const iconProps = {
    size: 50,
    round: true,
    bgStyle: {
      fill: "transparent",
      width: 100,
    },
    iconFillColor: "inherit",
  };

  const Copy = () => (
    <CopyToClipboard text={url}>
      <div css={{ position: "relative" }}>
        <button
          type="button"
          css={buttonCss}
          aria-label="Copy link"
          className="react-share__ShareButton"
          onClick={() => {
            popup && popup.current && popup.current.focus();
          }}
        >
          <svg
            viewBox="0 0 64 64"
            width={iconProps.size}
            height={iconProps.size}
          >
            <circle cx="32" cy="32" r="31" css={iconProps.bgStyle} />
            <LinkIcon x="15.5" y="15.5" />
          </svg>
        </button>
        <span
          ref={popup}
          tabIndex={-1}
          css={{
            position: "absolute",
            display: "block",
            padding: "0.3rem 0.5rem",
            bottom: "-2.5rem",
            transitionDuration: "0.3s",
            transitionProperty: "color background-color",
            color: "transparent",
            backgroundColor: "transparent",
            "&:focus": {
              color: "white",
              backgroundColor: color,
            },
            span: {
              visibility: "hidden",
              transitionDuration: "0s",
              transitionProperty: "visibility",
            },
            "&:focus > span": {
              visibility: "visible",
            },
          }}
        >
          <span>Copied!</span>
        </span>
      </div>
    </CopyToClipboard>
  );

  const Facebook = () => (
    <FacebookShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on Facebook"
      url={url}
    >
      <FacebookIcon {...iconProps} />
    </FacebookShareButton>
  );

  const Twitter = () => (
    <TwitterShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on Twitter"
      url={url}
      title={title}
    >
      <TwitterIcon {...iconProps} />
    </TwitterShareButton>
  );

  const Tumblr = () => (
    <TumblrShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on Tumblr"
      url={url}
      title={title}
    >
      <TumblrIcon {...iconProps} />
    </TumblrShareButton>
  );

  const LinkedIn = () => (
    <LinkedinShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on LinkedIn"
      url={url}
      title={title}
    >
      <LinkedinIcon {...iconProps} />
    </LinkedinShareButton>
  );

  const Telegram = () => (
    <TelegramShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on Telegram"
      url={url}
      title={title}
    >
      <TelegramIcon {...iconProps} size={40} />
    </TelegramShareButton>
  );

  const WhatsApp = () => (
    <WhatsappShareButton
      type="button"
      css={buttonCss}
      aria-label="Share on WhatsApp"
      url={url}
      title={title}
    >
      <WhatsappIcon {...iconProps} size={38} />
    </WhatsappShareButton>
  );

  const Email = () => (
    <EmailShareButton
      type="button"
      css={buttonCss}
      aria-label="Share by email"
      url={url}
      subject={title}
    >
      <EmailIcon {...iconProps} />
    </EmailShareButton>
  );

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        "& div": {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: "wrap",
        },
      }}
    >
      <div>
        <div>
          <div>
            <Copy />
          </div>
          <div>
            <Facebook />
          </div>
        </div>
        <div>
          <div>
            <Twitter />
          </div>
          <div>
            <Tumblr />
          </div>
        </div>
      </div>
      <div>
        <div>
          <div>
            <LinkedIn />
          </div>
          <div>
            <Telegram />
          </div>
        </div>
        <div>
          <div>
            <WhatsApp />
          </div>
          <div>
            <Email />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Share;
